import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowsLeftRight,
  ArrowUpRight,
  CheckCircle,
  LinkSimple,
  ListChecks,
  MagnifyingGlass,
  Plus,
  Target,
  Warning,
} from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { IEmpresaOption } from '../../../../../hooks/useContaCilImportar';
import { EmpresaPicker } from '../components/EmpresaPicker';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import {
  IContaCilRegra,
  useContaCilRegras,
  useContaCilRegrasBancos,
  useContaCilRegrasCategorias,
  useContaCilRegrasStats,
  useExcluirRegra,
  useToggleRegra,
} from '../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { agrupaPorCategoria, C } from './regrasHelpers';
import { OutrosRankingTab } from './components/OutrosRankingTab';
import { RegraDrawer } from './components/RegraDrawer';
import { RegrasIaTab } from './components/RegrasIaTab';
import { RegrasList } from './components/RegrasList';
import { propsMenuSelect } from '../../../../../utils/selectMenu';

interface KpiCardProps {
  label: string;
  value: string;
  sub?: string;
  accent: string;
  icon: React.ReactNode;
  onClick?: () => void;
  index: number;
}

function KpiCard({ label, value, sub, accent, icon, onClick, index }: KpiCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.42, delay: 0.06 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <GlassCard
        intensity="strong"
        p={5}
        hoverable={!!onClick}
        cursor={onClick ? 'pointer' : 'default'}
        tint={onClick ? accent : undefined}
        onClick={onClick}
      >
        <Flex justify="space-between" align="flex-start" mb={2}>
          <Text
            fontSize="10px"
            fontWeight="600"
            color={C.subtle}
            textTransform="uppercase"
            letterSpacing="0.08em"
          >
            {label}
          </Text>
          <Box
            w="32px"
            h="32px"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg={`${accent}22`}
            color={accent}
          >
            {icon}
          </Box>
        </Flex>
        <Flex align="center" gap={1}>
          <Text
            fontSize={{ base: '24px', md: '28px' }}
            fontWeight="700"
            letterSpacing="-0.02em"
            color={C.texto}
          >
            {value}
          </Text>
          {onClick && <ArrowUpRight size={16} color={accent} />}
        </Flex>
        {sub && (
          <Text fontSize="11px" fontWeight="600" color={accent} mt={0.5}>
            {sub}
          </Text>
        )}
      </GlassCard>
    </motion.div>
  );
}

interface DrawerState {
  open: boolean;
  mode: 'create' | 'edit' | 'duplicate';
  initial: Partial<IContaCilRegra> | null;
  editId: number | null;
}

const DRAWER_CLOSED: DrawerState = { open: false, mode: 'create', initial: null, editId: null };

const SKELE_KEYS = ['s1', 's2', 's3', 's4', 's5'];

export function ConciliacaoContabilRegrasPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  // Aplica `?empresa=<id>` (atalho vindo do botão "Regras" do Conciliar).
  useAplicarEmpresaParam(setEmpresa);
  const [bancoFilter, setBancoFilter] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'regras' | 'gaps' | 'ia'>('regras');
  const [drawer, setDrawer] = useState<DrawerState>(DRAWER_CLOSED);
  const [deleteRule, setDeleteRule] = useState<IContaCilRegra | null>(null);
  const [togglingSig, setTogglingSig] = useState<string | null>(null);

  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Catalogo completo (criar regra) vs. so os em uso (filtro da tela).
  const { data: bancosCatalogo = [] } = useContaCilRegrasBancos();
  const { data: bancosEmUso = [] } = useContaCilRegrasBancos({
    empresaId: empresa?.value,
    emUso: true,
  });
  const { data: categorias = [] } = useContaCilRegrasCategorias(empresa?.value);
  const { data: stats } = useContaCilRegrasStats(empresa?.value, bancoFilter);
  const {
    data: lista,
    isLoading: listaLoading,
    isFetching,
  } = useContaCilRegras({
    empresaId: empresa?.value,
    banco: bancoFilter,
    search,
  });

  const toggle = useToggleRegra();
  const excluir = useExcluirRegra();

  const grupos = useMemo(() => {
    if (!lista) return [];
    return agrupaPorCategoria([...lista.personalizadas, ...lista.hardcoded]);
  }, [lista]);

  const bancoOptions = useMemo(
    () => [
      { value: '', label: 'Todos os bancos' },
      // Regra so' escopa banco com slug canonico; bancos nao-reconhecidos
      // (slug vazio, ex. fintech fora do catalogo) nao entram aqui — so'
      // aparecem nos filtros de Exportar/Extratos, que filtram por nome.
      ...bancosEmUso.filter(b => b.slug).map(b => ({ value: b.slug, label: b.nome })),
    ],
    [bancosEmUso],
  );

  const abrirNova = () => setDrawer({ open: true, mode: 'create', initial: null, editId: null });
  const abrirPrefill = (prefill: Partial<IContaCilRegra>) =>
    setDrawer({ open: true, mode: 'create', initial: prefill, editId: null });
  const abrirEdicao = (r: IContaCilRegra) =>
    setDrawer({ open: true, mode: 'edit', initial: r, editId: Number(r.id) });
  const abrirDuplicar = (r: IContaCilRegra) =>
    setDrawer({
      open: true,
      mode: 'duplicate',
      initial: {
        kw: r.kw,
        categoria: r.categoria,
        dc: r.dc,
        banco: r.banco,
        poc_search: r.poc_search,
      },
      editId: null,
    });

  const handleToggle = async (r: IContaCilRegra) => {
    // Escopo do toggle = escopo da visão atual. Com empresa selecionada,
    // (des)ativa só para essa empresa (não nuke global, mesmo em regra global).
    // Sem empresa (catálogo), usa o escopo próprio da regra.
    const scope = empresa
      ? { empresa_id: empresa.value, banco: bancoFilter || null }
      : { empresa_id: r.empresa_id ?? null, banco: bancoFilter || null };
    setTogglingSig(r.kw_signature);
    try {
      await toggle.mutateAsync({ ...scope, kw_signature: r.kw_signature, ativar: !r.ativa });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Não foi possível alternar.'), status: 'error' });
    } finally {
      setTogglingSig(null);
    }
  };

  const confirmarExclusao = async () => {
    if (!deleteRule) return;
    try {
      await excluir.mutateAsync(Number(deleteRule.id));
      toast({ title: 'Regra excluída.', status: 'success' });
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, 'Não foi possível excluir.'), status: 'error' });
    } finally {
      setDeleteRule(null);
    }
  };

  const temEmpresa = empresa != null;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" justify="space-between" gap={3} mb={5} wrap="wrap">
            <Flex align="center" gap={3}>
              <IconButton
                aria-label="Voltar"
                icon={<ArrowLeft size={18} weight="bold" />}
                variant="ghost"
                size="sm"
                borderRadius="full"
                color={C.muted}
                _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: C.texto }}
                onClick={() => navigate('/modulesHome/accountingConciliation/conciliacao-contabil')}
              />
              <Box
                w="64px"
                h="64px"
                borderRadius="2xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)"
                color={C.rosa}
                boxShadow="0 8px 24px -4px rgba(236, 72, 153, 0.32)"
              >
                <ArrowsLeftRight size={34} weight="duotone" />
              </Box>
              <Box>
                <Text
                  fontSize="11px"
                  color={C.subtle}
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  fontWeight="semibold"
                >
                  Conciliação Contábil
                </Text>
                <Heading fontSize={{ base: '24px', md: '32px' }} fontWeight="700" color={C.texto}>
                  Regras de categorização
                </Heading>
                <Text fontSize="sm" color={C.muted}>
                  Histórico → categoria → POC. A POC fixa alimenta o auto-vínculo.
                </Text>
              </Box>
            </Flex>
            <Button
              leftIcon={<Plus size={18} weight="bold" />}
              borderRadius="full"
              px={6}
              bg={C.rosa}
              color="white"
              fontWeight="600"
              boxShadow="0 8px 20px -4px rgba(236,72,153,0.42)"
              _hover={{ bg: C.rosaHover, transform: 'translateY(-1px)' }}
              _active={{ bg: C.rosaHover }}
              onClick={abrirNova}
            >
              Nova regra
            </Button>
          </Flex>
        </motion.div>

        {/* KPI strip */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4} mb={5}>
          {!stats ? (
            SKELE_KEYS.map(k => (
              <GlassCard key={k} intensity="strong" p={5}>
                <Skeleton height="28px" width="60%" mt={4} borderRadius="md" />
              </GlassCard>
            ))
          ) : (
            <>
              <KpiCard
                index={0}
                label="Regras"
                value={String(stats.total)}
                sub={`${stats.hardcoded} padrão + ${stats.personalizadas} personalizadas`}
                accent={C.indigo}
                icon={<ListChecks size={18} weight="duotone" />}
              />
              <KpiCard
                index={1}
                label="Ativas"
                value={String(stats.ativas)}
                sub={stats.inativas > 0 ? `${stats.inativas} desativadas` : 'todas habilitadas'}
                accent={C.verde}
                icon={<CheckCircle size={18} weight="duotone" />}
              />
              <KpiCard
                index={2}
                label="Com POC"
                value={String(stats.com_poc)}
                sub={`${stats.percent_com_poc}% das regras`}
                accent={C.indigo}
                icon={<LinkSimple size={18} weight="duotone" />}
              />
              <KpiCard
                index={3}
                label="Cobertura"
                value={temEmpresa ? `${stats.cobertura_percent}%` : '—'}
                sub={temEmpresa ? 'lançamentos categorizados' : 'selecione empresa'}
                accent={C.brand}
                icon={<Target size={18} weight="duotone" />}
              />
              <KpiCard
                index={4}
                label='Gap "Outros"'
                value={temEmpresa ? `${stats.gap_outros_percent}%` : '—'}
                sub={temEmpresa ? `${stats.gap_outros_txns} sem regra` : 'selecione empresa'}
                accent={C.ambar}
                icon={<Warning size={18} weight="duotone" />}
                onClick={temEmpresa ? () => setTab('gaps') : undefined}
              />
            </>
          )}
        </SimpleGrid>

        {/* Filtros */}
        <GlassCard intensity="medium" p={4} mb={5}>
          <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ md: 'center' }}>
            <Box flex={1} minW={0}>
              <EmpresaPicker
                value={empresa}
                onChange={setEmpresa}
                label="Empresa (escopo + prévia)"
              />
            </Box>
            <Box w={{ base: 'full', md: '200px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color={C.muted}
                mb={1}
                textTransform="uppercase"
              >
                Banco
              </Text>
              <RSelect
                size="sm"
                options={bancoOptions}
                value={bancoOptions.find(o => o.value === (bancoFilter || '')) ?? bancoOptions[0]}
                onChange={opt => setBancoFilter((opt as { value: string })?.value || null)}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                {...propsMenuSelect}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Box w={{ base: 'full', md: '260px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color={C.muted}
                mb={1}
                textTransform="uppercase"
              >
                Buscar regra
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={14} color="#94A3B8" />
                </InputLeftElement>
                <Input
                  borderRadius="lg"
                  bg="white"
                  placeholder="keyword ou categoria..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Box>
          </Flex>
        </GlassCard>

        {/* Tabs */}
        <Flex
          display="inline-flex"
          bg="rgba(255,255,255,0.72)"
          backdropFilter="blur(20px) saturate(180%)"
          sx={{ WebkitBackdropFilter: 'blur(20px) saturate(180%)' }}
          borderRadius="full"
          p="4px"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.6)"
          mb={5}
          gap="2px"
        >
          {(
            [
              ['regras', `Regras${lista ? ` (${lista.total})` : ''}`, C.rosa],
              ['gaps', 'Gaps · Outros', C.ambar],
              ['ia', 'Regras IA', C.indigo],
            ] as ['regras' | 'gaps' | 'ia', string, string][]
          ).map(([val, label, color]) => (
            <Button
              key={val}
              size="sm"
              h="34px"
              px={4}
              borderRadius="full"
              variant="unstyled"
              fontWeight="600"
              bg={tab === val ? 'white' : 'transparent'}
              color={tab === val ? color : C.muted}
              boxShadow={tab === val ? '0 2px 8px -2px rgba(15,23,42,0.12)' : undefined}
              onClick={() => setTab(val)}
            >
              {label}
            </Button>
          ))}
        </Flex>

        {/* Conteúdo */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
          >
            {tab === 'regras' && (
              <>
                {isFetching && (
                  <Flex align="center" gap={2} mb={3}>
                    <Spinner size="xs" color={C.brand} thickness="2px" />
                    <Text fontSize="xs" color={C.muted}>
                      atualizando...
                    </Text>
                  </Flex>
                )}
                {listaLoading && (
                  <Flex direction="column" gap={3}>
                    {SKELE_KEYS.map(k => (
                      <GlassCard key={k} intensity="medium" p={4}>
                        <Skeleton height="20px" width="40%" borderRadius="md" />
                      </GlassCard>
                    ))}
                  </Flex>
                )}
                {!listaLoading && grupos.length === 0 && (
                  <GlassCard intensity="medium" p={10}>
                    <Text fontSize="sm" color={C.muted} textAlign="center">
                      Nenhuma regra para os filtros atuais.
                    </Text>
                  </GlassCard>
                )}
                {!listaLoading && grupos.length > 0 && (
                  <RegrasList
                    grupos={grupos}
                    onEdit={abrirEdicao}
                    onDuplicate={abrirDuplicar}
                    onAskDelete={setDeleteRule}
                    onToggle={handleToggle}
                    togglingSig={togglingSig}
                  />
                )}
              </>
            )}
            {tab === 'gaps' && (
              <OutrosRankingTab empresa={empresa} banco={bancoFilter} onCriarRegra={abrirPrefill} />
            )}
            {tab === 'ia' && <RegrasIaTab empresa={empresa} />}
          </motion.div>
        </AnimatePresence>
      </Box>

      <RegraDrawer
        isOpen={drawer.open}
        onClose={() => setDrawer(DRAWER_CLOSED)}
        empresa={empresa}
        bancos={bancosCatalogo}
        categorias={categorias}
        initial={drawer.initial}
        mode={drawer.mode}
        editId={drawer.editId}
        onSaved={() => undefined}
      />

      <AlertDialog
        isOpen={!!deleteRule}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteRule(null)}
        isCentered
      >
        <AlertDialogOverlay bg="rgba(15,23,42,0.45)" backdropFilter="blur(8px)">
          <AlertDialogContent borderRadius="2xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Excluir regra
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm" color={C.muted}>
              Lançamentos já categorizados não mudam; novos lançamentos deixam de usá-la.
              {deleteRule && (
                <Text mt={2} fontWeight="600" color={C.texto}>
                  {deleteRule.kw.join(', ')}
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                size="sm"
                variant="outline"
                onClick={() => setDeleteRule(null)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                ml={3}
                isLoading={excluir.isPending}
                onClick={confirmarExclusao}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassPageBg>
  );
}
